// src/pages/ProfilePage.jsx
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  updatePassword,
  reauthenticateWithCredential,
  linkWithCredential,
  EmailAuthProvider,
  signOut,
} from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { compressImage } from "../utils/compressImage";
import { auth, db, storage } from "../firebase";
import Layout from "../components/Layout";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useStudent } from "../hooks/useStudent";
import { fetchStudentDocs } from "../utils/fetchStudentDocs";
import { useTranslation } from "react-i18next";
import { translateCourseName } from "../utils/musicTerms";
import { SCHOOL } from "../config";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { student, course, refresh } = useStudent();
  const { t, i18n } = useTranslation();
  const [toast,   setToast]  = useState("");
  const [section, setSection]= useState(null); // 'password'

  // Check if user signed in with Google (no password exists)
  const isGoogleUser = auth.currentUser?.providerData
    ?.some(p => p.providerId === "google.com");

  function showToast(msg, err=false) {
    setToast(msg);
    setTimeout(()=>setToast(""),3000);
  }

  async function logout() {
    await signOut(auth);
    navigate("/login");
  }

  async function handleSwitchStudent() {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    try {
      const docs = await fetchStudentDocs(uid);
      const currentId = sessionStorage.getItem("activeStudentId") || uid;
      const others = docs.filter(d => d.id !== currentId);

      if (others.some(d => d.status === "active")) {
        sessionStorage.removeItem("activeStudentId");
        sessionStorage.removeItem("activeAuthUid");
        // Full reload, not navigate("/") — Guard (App.js) only re-evaluates
        // which profile to show inside a mount-time useEffect with an empty
        // dependency array, tied to Firebase's onAuthStateChanged. Navigating
        // between two routes that are both wrapped in Guard doesn't remount
        // it or fire that listener, so a client-side navigate() here silently
        // redisplays the same profile whenever Guard was already mounted —
        // only reloading the page guarantees the check actually reruns.
        window.location.href = "/";
      } else if (others.some(d => d.status === "pending_review")) {
        showToast("Family member request is pending for approval");
      } else {
        showToast("No family member account associated with this account");
      }
    } catch (e) {
      console.error("Switch student check failed:", e);
      showToast("Could not check family member accounts. Please try again.");
    }
  }

  return (
    <Layout title={`👤 ${t("profile.title")}`} student={student}>
      <div className="profile-grid"
           style={{ display:"grid",
                    gridTemplateColumns:"340px 1fr",gap:"20px",
                    alignItems:"start" }}>

        {/* LEFT — Profile card */}
        <div>
          <div className="card">
            <PhotoSection student={student} onSuccess={()=>{refresh();showToast(t("profile.photoUpdated"));}}/>
            <div className="divider"/>
            {student && (
              <div style={{ display:"flex",flexDirection:"column",gap:"10px" }}>
                {[
                  ["👤", t("profile.fullName"),  student.name],
                  ["📧", t("profile.email"),      auth.currentUser?.email],
                  ["📞", t("profile.phone"),      student.phone||"—"],
                  ["🎵", t("profile.course"),     course ? translateCourseName(course, i18n.language) : t("profile.notAssigned")],
                ].map(([icon,lbl,val])=>(
                  <div key={lbl} style={{ display:"flex",gap:"10px",
                                          padding:"8px 0",
                                          borderBottom:"1px solid #F3F4F6",
                                          fontSize:".9rem" }}>
                    <span style={{ width:"24px",textAlign:"center" }}>
                      {icon}
                    </span>
                    <span style={{ color:"var(--text-muted)",width:"90px",
                                   flexShrink:0 }}>
                      {lbl}
                    </span>
                    <span style={{ fontWeight:"500",wordBreak:"break-all" }}>
                      {val}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="divider"/>
            <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
              <button className="btn btn-outline btn-sm"
                style={{ width:"100%" }}
                onClick={handleSwitchStudent}>
                👥 Switch Student
              </button>
              <button className="btn btn-outline btn-sm"
                style={{ width:"100%" }}
                onClick={() => navigate("/register?sibling=true")}>
                ➕ Add Family Member
              </button>
              <button className="btn btn-danger btn-sm"
                style={{ width:"100%" }}
                onClick={logout}>
                🚪 {t("nav.logout")}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT — Settings */}
        <div>

          {/* Change / Add Password */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">🔑 {t("profile.changePassword")}</span>
              {isGoogleUser && section !== "addPassword" && (
                <button className="btn btn-outline btn-sm"
                  onClick={() => setSection("addPassword")}>
                  Add Password
                </button>
              )}
              {isGoogleUser && section === "addPassword" && (
                <button className="btn btn-outline btn-sm"
                  onClick={() => setSection(null)}>
                  {t("common.cancel")}
                </button>
              )}
              {!isGoogleUser && (
                <button className="btn btn-outline btn-sm"
                  onClick={() => setSection(section==="password" ? null : "password")}>
                  {section==="password" ? t("common.cancel") : t("common.change")}
                </button>
              )}
            </div>
            {isGoogleUser && section === "addPassword" ? (
              <LinkPasswordForm
                email={auth.currentUser?.email}
                onSuccess={() => {
                  setSection(null);
                  showToast("Password added! You can now sign in with email + password.");
                }}
              />
            ) : isGoogleUser ? (
              <div style={{ display:"flex", alignItems:"center", gap:"10px",
                            background:"#F0FDF4", border:"1px solid #BBF7D0",
                            borderRadius:"8px", padding:"12px 14px" }}>
                <span style={{ fontSize:"1.2rem" }}>🔐</span>
                <p className="muted" style={{ margin:0 }}>
                  Signed in with Google. Click <strong>Add Password</strong> to also sign in with email.
                </p>
              </div>
            ) : section === "password" ? (
              <ChangePasswordForm
                onSuccess={() => {
                  setSection(null);
                  showToast(t("profile.passwordChanged"));
                }}
              />
            ) : (
              <p className="muted">
                {t("profile.changePwdHint")}
              </p>
            )}
          </div>

          {/* Register a Sibling */}
          {student?.status === "active" && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">👨‍👧 Register a Family Member</span>
              </div>
              <p className="muted" style={{ marginBottom:"16px" }}>
                Add a family member (sibling, child, etc.) under the same login. Each member has separate course enrollment and billing.
              </p>
              <button className="btn btn-primary btn-sm"
                onClick={() => navigate("/register?sibling=true")}>
                + Register a Family Member
              </button>
            </div>
          )}

          {/* Language */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">🌐 {t("profile.appLanguage")}</span>
            </div>
            <p className="muted" style={{ marginBottom:"14px" }}>
              {t("profile.languageHint")}
            </p>
            <LanguageSwitcher />
          </div>

          {/* Contact Info */}
          <div className="card"
            style={{ background:"linear-gradient(135deg,var(--surface-2),var(--sb-stu-bg))",
                     border:"1px solid var(--border-warm)" }}>
            <div className="card-header">
              <span className="card-title">📞 {t("profile.needHelp")}</span>
            </div>
            <div style={{ display:"flex",flexDirection:"column",
                          gap:"10px",fontSize:".9rem" }}>
              {[
                ["📧", SCHOOL.email, `mailto:${SCHOOL.email}`],
                ["📞", SCHOOL.phone, `tel:${SCHOOL.phone.replace(/\s/g,"")}`],
              ].map(([icon,val,href])=>(
                <a key={val} href={href}
                  style={{ display:"flex",alignItems:"center",gap:"10px",
                           color:"var(--purple-text)",textDecoration:"none",
                           fontWeight:"500" }}>
                  <span>{icon}</span>
                  <span>{val}</span>
                </a>
              ))}
              <div style={{ display:"flex",gap:"10px",color:"var(--text-label)" }}>
                <span>📍</span>
                <span>{SCHOOL.address}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </Layout>
  );
}

/* ── Photo Upload Section ── */
function PhotoSection({ student, onSuccess }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [preview,   setPreview]   = useState(null);
  const fileRef = useRef();

  async function handleFile(e) {
    const file = e.target.files[0]; if(!file) return;
    // Photo must go on the ACTIVE profile's own doc — for a sibling that
    // differs from auth.currentUser.uid (the parent's login), and using
    // the login uid here would silently overwrite the parent's own
    // primary-account photo instead of the sibling's.
    if (!student?.id) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const sid         = student.id;
      const compressed = await compressImage(file, { maxWidth: 600, maxHeight: 600, quality: 0.80 });
      const sRef = ref(storage, `student_photos/${sid}.jpg`);
      await uploadBytes(sRef, compressed, { contentType: 'image/jpeg' });
      const url  = await getDownloadURL(sRef);
      await updateDoc(doc(db,"students",sid), { photoUrl: url });
      onSuccess();
    } catch(e){ console.error(e); }
    setUploading(false);
  }

  const photoSrc = preview || student?.photoUrl;

  return (
    <div style={{ display:"flex",flexDirection:"column",
                  alignItems:"center",marginBottom:"16px" }}>
      <div style={{ position:"relative",marginBottom:"12px" }}>
        <div style={{ width:"90px",height:"90px",borderRadius:"50%",
                      background:"#FBF0E8",border:"3px solid #F5E4D0",
                      display:"flex",alignItems:"center",
                      justifyContent:"center",fontSize:"2.2rem",
                      overflow:"hidden" }}>
          {photoSrc
            ? <img src={photoSrc} alt="profile" loading="lazy"
                style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
            : "👤"}
        </div>
        {/* Upload overlay button */}
        <button
          onClick={()=>fileRef.current.click()}
          disabled={uploading}
          style={{ position:"absolute",bottom:"0",right:"0",
                   width:"28px",height:"28px",borderRadius:"50%",
                   background:"#7B1515",color:"white",border:"2px solid white",
                   cursor:"pointer",fontSize:"14px",display:"flex",
                   alignItems:"center",justifyContent:"center" }}>
          {uploading ? "…" : "📷"}
        </button>
        <input ref={fileRef} type="file" accept="image/*"
          style={{ display:"none" }} onChange={handleFile}/>
      </div>
      <div style={{ fontWeight:"700",fontSize:"1rem" }}>
        {student?.name}
      </div>
      <div className="muted">{auth.currentUser?.email}</div>
      <button onClick={()=>fileRef.current.click()}
        disabled={uploading}
        className="btn btn-outline btn-sm"
        style={{ marginTop:"10px" }}>
        {uploading ? t("profile.uploading") : t("profile.changePhoto")}
      </button>
    </div>
  );
}

/* ── Link Password Form (Google users adding email/password) ── */
function LinkPasswordForm({ email, onSuccess }) {
  const [newPwd,  setNewPwd]  = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  async function submit(e) {
    e.preventDefault();
    if (newPwd !== confirm) { setError("Passwords do not match."); return; }
    if (newPwd.length < 6)  { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError("");
    try {
      const cred = EmailAuthProvider.credential(email, newPwd);
      await linkWithCredential(auth.currentUser, cred);
      onSuccess();
    } catch (e) {
      setError(e.code === "auth/provider-already-linked"
        ? "A password is already linked to this account."
        : "Failed to add password. Please try again.");
    }
    setLoading(false);
  }

  return (
    <form onSubmit={submit}>
      {error && <div className="error-box" style={{ marginBottom:"12px" }}>{error}</div>}
      <p className="muted" style={{ marginBottom:"14px", fontSize:".88rem" }}>
        Adding a password lets you sign in with <strong>{email}</strong> + password in addition to Google.
      </p>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">New Password</label>
          <input className="form-input" type="password"
            placeholder="Min 6 characters"
            value={newPwd} onChange={e => setNewPwd(e.target.value)} required/>
        </div>
        <div className="form-group">
          <label className="form-label">Confirm Password</label>
          <input className="form-input" type="password"
            placeholder="Repeat password"
            value={confirm} onChange={e => setConfirm(e.target.value)} required/>
        </div>
      </div>
      <button className="btn btn-primary" type="submit" disabled={loading}>
        {loading ? <span className="spinner"/> : "Add Password"}
      </button>
    </form>
  );
}

/* ── Change Password Form ── */
function ChangePasswordForm({ onSuccess }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [newPwd,  setNewPwd]  = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  async function submit(e) {
    e.preventDefault();
    if(newPwd!==confirm){ setError(t("profile.mismatch")); return; }
    if(newPwd.length<6)  { setError(t("profile.minRequired")); return; }
    setLoading(true); setError("");
    try {
      const user = auth.currentUser;
      const cred = EmailAuthProvider.credential(user.email, current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, newPwd);
      onSuccess();
    } catch(e) {
      setError(e.code==="auth/wrong-password"
        ? t("profile.wrongPassword")
        : t("profile.changeFailed"));
    }
    setLoading(false);
  }

  return (
    <form onSubmit={submit}>
      {error && <div className="error-box">{error}</div>}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">{t("profile.currentPassword")}</label>
          <input className="form-input" type="password"
            value={current} onChange={e=>setCurrent(e.target.value)} required/>
        </div>
        <div className="form-group" style={{ gridColumn:"1/-1" }}>
          {/* spacer for layout */}
        </div>
        <div className="form-group">
          <label className="form-label">{t("profile.newPassword")}</label>
          <input className="form-input" type="password"
            placeholder={t("profile.minChars")}
            value={newPwd} onChange={e=>setNewPwd(e.target.value)} required/>
        </div>
        <div className="form-group">
          <label className="form-label">{t("profile.confirmNewPassword")}</label>
          <input className="form-input" type="password"
            placeholder={t("profile.repeatPassword")}
            value={confirm} onChange={e=>setConfirm(e.target.value)} required/>
        </div>
      </div>
      <button type="submit" className="btn btn-primary"
        disabled={loading}>
        {loading ? <span className="spinner"/> : t("profile.updatePassword")}
      </button>
    </form>
  );
}
